import { useRef, useMemo, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useSlotTheme } from '../../hooks/useSlotTheme';
import Symbol3D from './Symbol3D';
import type { SymbolId } from '../../themes/types';

// =============================================================================
// Constants
// =============================================================================

const SYMBOL_HEIGHT = 1.0; // Height of each symbol slot
const VISIBLE_SYMBOLS = 3; // Number of visible symbols per reel
const TOTAL_REEL_SYMBOLS = 12; // Total symbols on the reel strip for smooth animation
const REEL_CIRCUMFERENCE = TOTAL_REEL_SYMBOLS * SYMBOL_HEIGHT;

// =============================================================================
// Types
// =============================================================================

export type ReelState = 'idle' | 'spinning' | 'stopping' | 'stopped';

interface ReelProps {
  /** Reel index (0-4) */
  index: number;
  /** X position offset */
  xPosition: number;
  /** Current reel state */
  state: ReelState;
  /** Final symbols to display when stopped [top, middle, bottom] */
  finalSymbols: SymbolId[];
  /** Winning symbol positions (row indices) */
  winningPositions?: number[];
  /** Callback when reel finishes stopping */
  onStopped?: () => void;
  /** Spin speed multiplier */
  speedMultiplier?: number;
}

// =============================================================================
// Reel Component
// =============================================================================

export function Reel({
  xPosition,
  state,
  finalSymbols,
  winningPositions = [],
  onStopped,
  speedMultiplier = 1,
}: ReelProps) {
  const { theme } = useSlotTheme();
  const groupRef = useRef<THREE.Group>(null);

  // Animation state
  const animationState = useRef({
    rotation: 0,
    velocity: 0,
    targetRotation: 0,
    startTime: 0,
    phase: 'idle' as 'idle' | 'accelerating' | 'fullSpeed' | 'decelerating' | 'bouncing',
  });

  // Generate reel strip symbols
  const reelSymbols = useMemo(() => {
    // Use the theme's reel strip but repeat to fill the cylinder
    const strip: SymbolId[] = [];
    for (let i = 0; i < TOTAL_REEL_SYMBOLS; i++) {
      const stripIndex = i % theme.reelStrip.length;
      strip.push(theme.reelStrip[stripIndex]);
    }
    return strip;
  }, [theme.reelStrip]);

  // Calculate final rotation when stopping
  useEffect(() => {
    if (state === 'stopping') {
      const anim = animationState.current;

      // Calculate target rotation to land on the correct symbols
      // We need to align the middle symbol (finalSymbols[1]) in the center
      const currentRotation = anim.rotation % REEL_CIRCUMFERENCE;
      const targetPosition = REEL_CIRCUMFERENCE - SYMBOL_HEIGHT; // Align to stop position

      // Add extra rotations for dramatic effect (2-3 full rotations)
      const extraRotations = (2 + Math.random()) * REEL_CIRCUMFERENCE;
      anim.targetRotation = anim.rotation + extraRotations + (targetPosition - currentRotation);
      anim.phase = 'decelerating';
    } else if (state === 'spinning') {
      animationState.current.phase = 'accelerating';
      animationState.current.startTime = Date.now();
    }
  }, [state]);

  // Animation frame
  useFrame((_, delta) => {
    if (!groupRef.current) return;

    const anim = animationState.current;
    const config = theme.animations;
    const baseSpeed = 15 * speedMultiplier;

    switch (anim.phase) {
      case 'accelerating': {
        // Ramp up speed
        const elapsed = Date.now() - anim.startTime;
        const progress = Math.min(elapsed / 300, 1);
        // Ease in quad
        const easedProgress = progress * progress;
        anim.velocity = baseSpeed * easedProgress;

        if (progress >= 1) {
          anim.phase = 'fullSpeed';
        }
        break;
      }

      case 'fullSpeed': {
        anim.velocity = baseSpeed;
        break;
      }

      case 'decelerating': {
        // Calculate progress toward target
        const remaining = anim.targetRotation - anim.rotation;

        if (remaining <= 0.1) {
          // Snap to target and start bounce
          anim.rotation = anim.targetRotation;
          anim.velocity = 0;
          anim.phase = 'bouncing';
          anim.startTime = Date.now();
        } else {
          // Ease out - slow down as we approach target
          const progress = 1 - remaining / (3 * REEL_CIRCUMFERENCE);
          const easedSpeed = baseSpeed * Math.max(0.2, 1 - progress * progress);
          anim.velocity = easedSpeed;
        }
        break;
      }

      case 'bouncing': {
        // Quick bounce effect when stopping
        const elapsed = Date.now() - anim.startTime;
        const bounceDuration = 200;
        const bounceAmount = config.reelBounceIntensity ?? 0.15;

        if (elapsed < bounceDuration) {
          const progress = elapsed / bounceDuration;
          // Bounce curve: sin wave that decays
          const bounce = Math.sin(progress * Math.PI * 2) * bounceAmount * (1 - progress);
          groupRef.current.position.y = bounce;
        } else {
          groupRef.current.position.y = 0;
          anim.phase = 'idle';
          onStopped?.();
        }
        break;
      }

      case 'idle':
      default:
        anim.velocity = 0;
        break;
    }

    // Apply rotation
    anim.rotation += anim.velocity * delta;
    groupRef.current.rotation.x = anim.rotation;
  });

  // Determine which symbols to show based on state
  const displaySymbols = useMemo(() => {
    if (state === 'stopped' || state === 'idle') {
      // Show final symbols
      return finalSymbols;
    }
    // During spin, show reel strip symbols (they'll blur together)
    return reelSymbols.slice(0, VISIBLE_SYMBOLS);
  }, [state, finalSymbols, reelSymbols]);

  return (
    <group position={[xPosition, 0, 0]}>
      {/* Reel cylinder */}
      <group ref={groupRef}>
        {state === 'stopped' || state === 'idle' ? (
          // Static display of final symbols
          <StaticSymbols
            symbols={displaySymbols}
            winningPositions={winningPositions}
          />
        ) : (
          // Spinning reel strip
          <SpinningReel symbols={reelSymbols} />
        )}
      </group>

      {/* Reel frame/border */}
      <ReelFrame />
    </group>
  );
}

// =============================================================================
// Static Symbols (when reel is stopped)
// =============================================================================

interface StaticSymbolsProps {
  symbols: SymbolId[];
  winningPositions: number[];
}

function StaticSymbols({ symbols, winningPositions }: StaticSymbolsProps) {
  return (
    <>
      {symbols.map((symbolId, row) => {
        const yPosition = (1 - row) * SYMBOL_HEIGHT; // Top to bottom
        const isWinning = winningPositions.includes(row);

        return (
          <Symbol3D
            key={`static-${row}`}
            symbolId={symbolId}
            position={[0, yPosition, 0]}
            isWinning={isWinning}
            glowIntensity={isWinning ? 0.8 : 0}
          />
        );
      })}
    </>
  );
}

// =============================================================================
// Spinning Reel (during spin animation)
// =============================================================================

interface SpinningReelProps {
  symbols: SymbolId[];
}

function SpinningReel({ symbols }: SpinningReelProps) {
  // Arrange symbols in a cylinder
  return (
    <>
      {symbols.map((symbolId, i) => {
        // Calculate position on cylinder
        const angle = (i / symbols.length) * Math.PI * 2;
        const radius = REEL_CIRCUMFERENCE / (2 * Math.PI);
        const y = Math.cos(angle) * radius;
        const z = Math.sin(angle) * radius;

        // Only render symbols that would be visible
        if (z < -radius * 0.3) return null;

        return (
          <group
            key={`spin-${i}`}
            position={[0, y, z]}
            rotation={[-angle, 0, 0]}
          >
            <Symbol3D symbolId={symbolId} position={[0, 0, 0]} />
          </group>
        );
      })}
    </>
  );
}

// =============================================================================
// Reel Frame
// =============================================================================

function ReelFrame() {
  const { theme } = useSlotTheme();

  return (
    <group>
      {/* Top border */}
      <mesh position={[0, SYMBOL_HEIGHT * 1.5 + 0.05, 0.1]}>
        <boxGeometry args={[1.1, 0.1, 0.05]} />
        <meshStandardMaterial
          color={theme.style.secondary}
          metalness={0.8}
          roughness={0.2}
        />
      </mesh>

      {/* Bottom border */}
      <mesh position={[0, -SYMBOL_HEIGHT * 1.5 - 0.05, 0.1]}>
        <boxGeometry args={[1.1, 0.1, 0.05]} />
        <meshStandardMaterial
          color={theme.style.secondary}
          metalness={0.8}
          roughness={0.2}
        />
      </mesh>
    </group>
  );
}

export default Reel;
